/**
 * Class representing a customizable button component.
 * @category Components
 * @subcategory ui
 */
class ButtonComponent {
  /**
   * Constructor for ButtonComponent.
   * @param {Object} options - Configuration options for the button.
   * @param {string} [options.label=""] - The button label.
   * @param {string} [options.imgSrc=null] - The image source for an optional icon.
   * @param {string} [options.tooltip=""] - Tooltip text for the button.
   * @param {Function} options.onClick - Callback function for click events.
   * @param {string|Array} [options.className=""] - CSS class or array of classes to apply.
   * @param {boolean} [options.disabled=false] - Disabled state of the button.
   */
  constructor({
    label = "",
    imgSrc = null,
    tooltip = "",
    onClick,
    className = "",
    disabled = false,
  }) {
    /**
     * @param {HTMLElement} - The DOM element representing the button.
     */
    this.button = this.createButton(label, imgSrc, tooltip, className);
    this.eventListeners = []; // Track added event listeners for disposal

    if (onClick) this.addEventListeners("click", onClick);
    if (disabled) this.disable();
  }


  cleanup() {
    console.log("✅ Cleaning up ButtonComponent...");

    // Remove all event listeners attached to the button
    this.eventListeners.forEach(({ eventType, callback }) => {
        this.button.removeEventListener(eventType, callback);
    });

    // Clear event listeners array
    this.eventListeners = [];

    // Remove the button element from the DOM
    if (this.button && this.button.parentNode) {
        this.button.parentNode.removeChild(this.button);
    }

    // Nullify references for garbage collection
    this.button = null;

    console.log("✅ ButtonComponent cleanup complete.");
}

  /**
   * Creates the button element with specified attributes.
   * @param {string} label - Button text label.
   * @param {string|null} imgSrc - Source for an optional image icon.
   * @param {string} tooltip - Tooltip text.
   * @param {string|Array} className - CSS classes to apply.
   * @returns {HTMLElement} - The created button element.
   */
  createButton(label, imgSrc, tooltip, className) {
    const button = document.createElement("button");

    // Handle array or string for className
    const classes = Array.isArray(className) ? className : [className];
    button.classList.add(...classes);

    // Set tooltip if provided
    if (tooltip) button.title = tooltip;

    // Append image if provided
    if (imgSrc) {
      const img = document.createElement("img");
      img.src = imgSrc;
      img.alt = label || "button image";
      img.classList.add("btn-img");
      button.appendChild(img);
    }

    // Append text if provided
    if (label) button.appendChild(document.createTextNode(label));

    return button;
  }

  /**
   * Adds event listeners to the button and tracks them for disposal.
   * @param {string} eventType - Type of event to listen for (e.g., "click").
   * @param {Function} callback - Event handler function.
   */
  addEventListeners(eventType, callback) {
    this.button.addEventListener(eventType, callback);
    this.eventListeners.push({ eventType, callback });
  }

  /**
   * Enables the button for interaction.
   */
  enable() {
    this.button.disabled = false;
  }

  /**
   * Disables the button, making it non-interactive.
   */
  disable() {
    this.button.disabled = true;
  }

  /**
   * Disposes of the button by removing event listeners and the element.
   */
  dispose() {
    // Remove all tracked event listeners
    this.eventListeners.forEach(({ eventType, callback }) => {
      this.button.removeEventListener(eventType, callback);
    });
    this.eventListeners = []; // Clear tracked listeners
    this.button.remove();
  }
}

export default ButtonComponent;
